import {
  Avatar,
  Box,
  Checkbox,
  CheckboxGroup,
  Heading,
  Icon,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Spinner,
  Tab,
  TabList,
  Tabs,
  Tag,
  TagLabel,
  Text,
  useClipboard,
  useDisclosure,
  Flex,
} from "@chakra-ui/react";
import {
  FaShare,
  FaUsers,
  FaShieldAlt,
  FaHeart,
  FaUserSecret,
  FaCheckCircle,
} from "react-icons/fa";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import { Button } from "../components/Button";
import { colors } from "../colors";
import skipGiveIcon from "../assets/skipgive-icon.png";
import { Image } from "../components/Reusable";
import { useViewport } from "../hooks/useViewport";
import { Show } from "../components/Show";
import { useUrlParams } from "../hooks/useUrlParams";
import { GetCampaignResponse, getCampaign } from "../api/campaign/getCampaign";
import { formatNumber } from "../utils/format";
import {
  PostCheckoutSessionRequest,
  getCheckoutSession,
} from "../api/pay/checkoutSession";
import { useShowToast } from "../hooks/useShowToast";
import { OtherAmount } from "../components/OtherAmount";
import { RecentDonationsTicker } from "../components/RecentDonations";
import { AlertMessage } from "../components/AlertMessage";
import { CountdownTimer } from "../components/CountdownTimer";
import { Badge } from "../components/Badge";
import { ProgressBar } from "../components/ProgressBar";

const PageWrapper = styled.div<{ $isMobile: boolean; $themeColor?: string }>`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    ${(props) => (props.$themeColor ?? colors.primary) + "15"} 0%,
    ${(props) => (props.$themeColor ?? colors.primary) + "05"} 50%,
    #ffffff 100%
  );
  width: 100%;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
`;

const Header = styled.header<{ $isMobile: boolean; $themeColor?: string }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: linear-gradient(
    90deg,
    ${(props) => props.$themeColor ?? colors.primary} 0%,
    ${(props) => (props.$themeColor ?? colors.primary) + "f0"} 100%
  );
  backdrop-filter: blur(20px);
  box-shadow: 0 2px 12px
    ${(props) => (props.$themeColor ?? colors.primary) + "30"};
  padding: ${(props) => (props.$isMobile ? "0.75rem 1rem" : "0.75rem 2rem")};
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const OrgInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  img {
    border-radius: 0.5rem;
  }
`;

const OrgName = styled.p<{ $isMobile: boolean }>`
  font-size: ${(props) => (props.$isMobile ? "0.9rem" : "1rem")};
  font-weight: 600;
  color: white;
`;

const MainContent = styled.main<{ $isMobile: boolean }>`
  padding-top: ${(props) => (props.$isMobile ? "4rem" : "4.5rem")};
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: ${(props) => (props.$isMobile ? "1fr" : "1fr 480px")};
  gap: ${(props) => (props.$isMobile ? "0" : "2rem")};
  padding-left: ${(props) => (props.$isMobile ? "0" : "2rem")};
  padding-right: ${(props) => (props.$isMobile ? "0" : "2rem")};
  padding-bottom: ${(props) => (props.$isMobile ? "0" : "3rem")};
`;

const CampaignInfoSection = styled.section<{ $isMobile: boolean }>`
  padding: ${(props) => (props.$isMobile ? "2rem 1.5rem 0" : "3rem 0")};
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const CampaignLogo = styled.div`
  margin: 0 auto 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    border-radius: 0.75rem;
  }
`;

const CampaignTitle = styled.h1<{ $isMobile: boolean }>`
  font-size: ${(props) => (props.$isMobile ? "2rem" : "3rem")};
  font-weight: 700;
  color: ${colors.black};
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const CampaignDescription = styled.p<{ $isMobile: boolean }>`
  font-size: ${(props) => (props.$isMobile ? "1.05rem" : "1.25rem")};
  color: ${colors.black}99;
  line-height: 1.6;
  max-width: 700px;
  margin: 0 auto;
`;

const StatsCard = styled.div<{ $themeColor?: string; $isMobile: boolean }>`
  background: white;
  border-radius: 1.5rem;
  padding: 0;
  box-shadow: 0 8px 24px
    ${(props) => (props.$themeColor ?? colors.primary) + "20"};
  margin: 0 auto 2rem;
  border: 2px solid ${(props) => (props.$themeColor ?? colors.primary) + "40"};
  overflow: hidden;
  max-width: ${(props) => (props.$isMobile ? "100%" : "600px")};
  width: 100%;
`;

const StatsHeader = styled.div<{ $themeColor?: string }>`
  background: linear-gradient(
    135deg,
    ${(props) => props.$themeColor ?? colors.primary} 0%,
    ${(props) => props.$themeColor ?? colors.primary}d9 25%,
    ${(props) => props.$themeColor ?? colors.primary}99 60%,
    ${(props) => props.$themeColor ?? colors.primary}73 100%
  );
  padding: 2rem;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -80%;
    right: -30%;
    width: 300px;
    height: 300px;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.25) 0%,
      transparent 65%
    );
    border-radius: 50%;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -60%;
    left: -20%;
    width: 250px;
    height: 250px;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.2) 0%,
      rgba(255, 255, 255, 0.05) 50%,
      transparent 70%
    );
    border-radius: 50%;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const AmountRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const RaisedAmount = styled.div<{ $isMobile: boolean }>`
  font-size: ${(props) => (props.$isMobile ? "2.25rem" : "3rem")};
  font-weight: 700;
  color: white;
`;

const GoalAmount = styled.div<{ $progressBarColor?: string }>`
  font-size: 1.15rem;
  color: ${(props) => props.$progressBarColor ?? "white"};
  opacity: 0.9;
  font-weight: 600;
`;

const ProgressBarContainer = styled.div<{ $themeColor?: string }>`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const ParticipantsCount = styled.div<{ $iconColor?: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  opacity: 0.95;
  font-size: 0.95rem;
  margin-top: 0.75rem;

  svg {
    color: ${(props) => props.$iconColor ?? "white"};
  }
`;

const DonationCard = styled.div<{ $isMobile: boolean; $themeColor?: string }>`
  background: linear-gradient(
    135deg,
    ${(props) => (props.$themeColor ?? colors.primary) + "30"} 0%,
    ${(props) => (props.$themeColor ?? colors.primary) + "20"} 100%
  );
  border-radius: ${(props) =>
    props.$isMobile ? "1.5rem 1.5rem 0 0" : "1.5rem"};
  padding: ${(props) => (props.$isMobile ? "2.5rem 1.5rem 3rem" : "2.5rem")};
  box-shadow: ${(props) =>
    props.$isMobile
      ? "none"
      : `0 8px 32px ${(props.$themeColor ?? colors.primary) + "30"}`};
  position: ${(props) => (props.$isMobile ? "relative" : "sticky")};
  top: ${(props) => (props.$isMobile ? "0" : "7.5rem")};
  margin: ${(props) => (props.$isMobile ? "0" : "1rem 0")};
  border: ${(props) =>
    props.$isMobile
      ? "none"
      : `2px solid ${(props.$themeColor ?? colors.primary) + "60"}`};
  width: 100%;
  min-height: ${(props) => (props.$isMobile ? "100vh" : "auto")};
`;

const DonationCardHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const DonationCardTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${colors.black};
  margin-bottom: 0.5rem;
`;

const AmountGrid = styled.div<{ $isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)"};
  gap: 0.75rem;
  margin: 1.5rem 0;
`;

const AmountButton = styled.button<{
  $themeColor?: string;
  $isOther?: boolean;
}>`
  background: ${(props) =>
    props.$isOther
      ? "rgba(255, 255, 255, 0.6)"
      : (props.$themeColor ?? colors.primary) + "40"};
  border: 2px solid
    ${(props) =>
      (props.$themeColor ?? colors.primary) + (props.$isOther ? "90" : "70")};
  border-radius: 1rem;
  padding: 1.25rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${colors.black};
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: ${(props) =>
      (props.$themeColor ?? colors.primary) + (props.$isOther ? "50" : "60")};
    border-color: ${(props) => props.$themeColor ?? colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 6px 16px
      ${(props) => (props.$themeColor ?? colors.primary) + "40"};
  }

  &:active {
    transform: translateY(0);
  }
`;

const OptionsSection = styled.div<{ $themeColor?: string }>`
  margin: 1.5rem 0;
  padding: 0;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  border-radius: 1rem;
  border: 1px solid ${(props) => (props.$themeColor ?? colors.primary) + "30"};
  overflow: hidden;
`;

const OptionRow = styled.div<{
  $hasBottom?: boolean;
  $isMobile?: boolean;
  $isMiddleRow?: boolean;
}>`
  display: flex;
  align-items: ${(props) =>
    props.$isMobile && props.$isMiddleRow ? "flex-start" : "center"};
  justify-content: space-between;
  padding: ${(props) =>
    props.$isMobile && props.$isMiddleRow ? "1.25rem 1.25rem" : "1rem 1.25rem"};
  gap: 0.75rem;
  border-bottom: ${(props) =>
    props.$hasBottom ? `1px solid ${colors.black}08` : "none"};
  transition: background 0.2s ease;
  cursor: pointer;
  flex-wrap: ${(props) =>
    props.$isMobile && props.$isMiddleRow ? "wrap" : "nowrap"};

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const OptionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
`;

const OptionIcon = styled.div<{ $themeColor?: string }>`
  width: 36px;
  height: 36px;
  border-radius: 0.5rem;
  background: ${(props) => (props.$themeColor ?? colors.primary) + "15"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$themeColor ?? colors.primary};
  font-size: 1rem;
  flex-shrink: 0;
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionLabel = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${colors.black};
  margin-bottom: 0.15rem;
`;

const OptionDescription = styled.div`
  font-size: 0.8rem;
  color: ${colors.black}70;
  line-height: 1.3;
`;

const TipModalContent = styled.div`
  text-align: center;
  padding: 1rem 0;
  position: relative;
  z-index: 1;
`;

const TipModalLogo = styled.div<{ $themeColor?: string }>`
  margin: 0 auto 1.5rem;
  display: flex;
  justify-content: center;
  width: 100px;
  height: 100px;
  background: white;
  border-radius: 1.5rem;
  padding: 0.75rem;
  box-shadow: 0 4px 16px ${(props) => (props.$themeColor ?? colors.primary) + "30"};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const TipModalTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${colors.black};
  margin-bottom: 0.75rem;
`;

const TipModalDescription = styled.p`
  font-size: 1rem;
  color: ${colors.black}90;
  line-height: 1.6;
  margin-bottom: 2rem;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const TipOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
`;

const TipOptionButton = styled.button<{
  $isSelected?: boolean;
  $themeColor?: string;
  $isNoTip?: boolean;
}>`
  padding: ${(props) => (props.$isNoTip ? "0.75rem" : "1.25rem")};
  border-radius: 1rem;
  border: 2px solid
    ${(props) =>
      props.$isSelected
        ? props.$themeColor ?? colors.primary
        : props.$isNoTip
        ? colors.black + "20"
        : (props.$themeColor ?? colors.primary) + "40"};
  background: ${(props) =>
    props.$isSelected
      ? props.$themeColor ?? colors.primary
      : props.$isNoTip
      ? "transparent"
      : "white"};
  font-size: ${(props) => (props.$isNoTip ? "0.9rem" : "1.5rem")};
  font-weight: ${(props) => (props.$isNoTip ? "600" : "700")};
  color: ${(props) =>
    props.$isSelected
      ? "white"
      : props.$isNoTip
      ? colors.black + "70"
      : colors.black};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => props.$themeColor ?? colors.primary};
    background: ${(props) =>
      props.$isNoTip
        ? colors.black + "05"
        : props.$isSelected
        ? props.$themeColor ?? colors.primary
        : (props.$themeColor ?? colors.primary) + "30"};
  }
`;

const StyledModalContent = styled.div<{ $themeColor?: string; $isMobile: boolean }>`
  background: linear-gradient(
    135deg,
    ${(props) => (props.$themeColor ?? colors.primary) + "08"} 0%,
    ${(props) => (props.$themeColor ?? colors.primary) + "15"} 50%,
    ${(props) => (props.$themeColor ?? colors.primary) + "08"} 100%
  );
  max-width: ${(props) => (props.$isMobile ? "100vw" : "500px")};
  max-height: ${(props) => (props.$isMobile ? "100vh" : "auto")};
  height: ${(props) => (props.$isMobile ? "100vh" : "auto")};
  margin: ${(props) => (props.$isMobile ? 0 : "1rem")};
  border-radius: ${(props) => (props.$isMobile ? 0 : "1rem")};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -10%;
    width: 200px;
    height: 200px;
    background: radial-gradient(
      circle,
      ${(props) => (props.$themeColor ?? colors.primary) + "20"} 0%,
      transparent 70%
    );
    border-radius: 50%;
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -50%;
    left: -10%;
    width: 200px;
    height: 200px;
    background: radial-gradient(
      circle,
      ${(props) => (props.$themeColor ?? colors.primary) + "15"} 0%,
      transparent 70%
    );
    border-radius: 50%;
    z-index: 0;
  }
`;

const ShareSection = styled.div<{ $themeColor?: string }>`
  text-align: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid
    ${(props) => (props.$themeColor ?? colors.primary) + "40"};
`;

const ShareButton = styled.button<{
  $themeColor?: string;
  $variant?: "primary" | "secondary";
}>`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: ${(props) =>
    props.$variant === "primary" ? "1rem 2rem" : "0.75rem 1.5rem"};
  font-size: ${(props) =>
    props.$variant === "primary" ? "1.05rem" : "0.95rem"};
  font-weight: 600;
  color: ${(props) => (props.$variant === "primary" ? "white" : colors.black)};
  background: ${(props) =>
    props.$variant === "primary"
      ? props.$themeColor ?? colors.primary
      : "rgba(255, 255, 255, 0.7)"};
  border: 2px solid
    ${(props) =>
      props.$variant === "primary"
        ? "transparent"
        : (props.$themeColor ?? colors.primary) + "60"};
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px
      ${(props) => (props.$themeColor ?? colors.primary) + "40"};
    opacity: ${(props) => (props.$variant === "primary" ? "0.9" : "1")};
    border-color: ${(props) =>
      props.$variant === "primary"
        ? "transparent"
        : props.$themeColor ?? colors.primary};
    background: ${(props) =>
      props.$variant === "primary"
        ? props.$themeColor ?? colors.primary
        : "rgba(255, 255, 255, 0.9)"};
  }
`;

const RecentDonationsSection = styled.div`
  padding: 1.5rem 2rem;
  background: ${colors.black}02;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BonusBadge = styled.div<{ $themeColor?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${(props) => props.$themeColor ?? colors.primary};
  color: white;
  padding: 0.5rem 1.25rem;
  border-radius: 2rem;
  font-weight: 600;
  font-size: 0.95rem;
  margin: 1.5rem 0;
  box-shadow: 0 2px 8px
    ${(props) => (props.$themeColor ?? colors.primary) + "40"};
`;

const SuccessCard = styled.div`
  text-align: center;
  padding: 3rem 2rem;
`;

const SuccessIconWrapper = styled.div<{ $themeColor?: string }>`
  font-size: 4rem;
  margin-bottom: 1rem;
  color: ${(props) => props.$themeColor ?? colors.primary};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SuccessTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${colors.black};
  margin-bottom: 0.5rem;
`;

const SuccessText = styled.p`
  font-size: 1.15rem;
  color: ${colors.black}80;
  margin-bottom: 2rem;
`;

const PoweredByBadge = styled.div<{ $isMobile: boolean }>`
  margin-top: ${(props) => (props.$isMobile ? "2rem" : "3rem")};
  display: flex;
  justify-content: center;
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  width: 100%;
`;

const LoadingPage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
`;

type GiveInterval = "one-time" | "monthly";

export const CampaignV2 = () => {
  const [giveInterval, setGiveInterval] = useState<GiveInterval>("one-time");
  const { isMobile } = useViewport();
  const navigate = useNavigate();
  const donateRef = React.useRef<HTMLDivElement>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isCoveringFees, setIsCoveringFees] = useState(true);
  const [giveAnon, setGiveAnon] = useState(false);
  const [gettingLink, setGettingLink] = useState(false);
  const [campaignDetails, setCampaignDetails] = useState<
    GetCampaignResponse | undefined
  >(undefined);
  const { showToast } = useShowToast();
  const [selectedTipPercentage, setSelectedTipPercentage] = useState(10);
  const [showOtherAmount, setShowOtherAmount] = useState(false);
  const {
    isOpen: isTipModalOpen,
    onOpen: onTipModalOpen,
    onClose: onTipModalClose,
  } = useDisclosure();

  const { onCopy } = useClipboard(
    campaignDetails?.shortUrl || window.location.href.split("?")[0]
  );

  const isTipping = selectedTipPercentage > 0;

  const { queryParams, routeParams } = useUrlParams();

  useEffect(() => {
    if (routeParams.id) {
      getCampaign(routeParams.id).then((campaign) => {
        setCampaignDetails(campaign);
      });
    }

    if (queryParams.paymentSuccess === "true") {
      setShowSuccess(true);
    }
  }, [queryParams.paymentSuccess, routeParams.id]);

  const scrollToDonate = () => {
    if (donateRef.current) {
      const headerHeight = isMobile ? 64 : 60; // Approximate header height
      const elementPosition = donateRef.current.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handlePayment = async (amount: number) => {
    if (!routeParams?.id) return;

    setGettingLink(true);
    const kissTipPercentage = isTipping ? selectedTipPercentage / 100 : 0;
    const kissTip = Math.round(amount * kissTipPercentage * 100);
    const data: PostCheckoutSessionRequest = {
      campaignId: routeParams.id,
      amount: amount * 100,
      isMonthly: giveInterval === "monthly",
      isCoveringFees: isCoveringFees,
      tipAmount: kissTip,
      isAnon: giveAnon,
    };

    const secret = await getCheckoutSession(data);
    if (secret) {
      navigate(`/campaign/${routeParams.id}/checkout?clientSecret=${secret}`);
    }
  };

  const handleShareClick = () => {
    onCopy();
    showToast("Share link copied to the clipboard", "success");
  };

  const handleTipSelection = (percentage: number) => {
    setSelectedTipPercentage(percentage);
    onTipModalClose();
  };

  const totalRaised = Math.round(
    (campaignDetails?.totalRaised ?? 0) + (campaignDetails?.bonus ?? 0)
  );

  // Create a darker version of the theme color for the progress bar
  const darkenColor = (hexColor: string): string => {
    // Remove # if present
    const hex = hexColor.replace("#", "");
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    // Darken by 15%
    const darkenedR = Math.round(r * 0.85);
    const darkenedG = Math.round(g * 0.85);
    const darkenedB = Math.round(b * 0.85);
    // Convert back to hex
    return `#${darkenedR.toString(16).padStart(2, "0")}${darkenedG
      .toString(16)
      .padStart(2, "0")}${darkenedB.toString(16).padStart(2, "0")}`;
  };

  const progressBarColor = campaignDetails?.colorTheme
    ? darkenColor(campaignDetails.colorTheme)
    : darkenColor(colors.primary);

  const renderAmountButtons = () => {
    const amounts = [25, 50, 100, 250, 500];
    return (
      <>
        {amounts.map((amount) => (
          <AmountButton
            key={amount}
            $themeColor={campaignDetails?.colorTheme}
            onClick={() => handlePayment(amount)}
          >
            ${amount}
          </AmountButton>
        ))}
        <AmountButton
          $themeColor={campaignDetails?.colorTheme}
          $isOther
          onClick={() => setShowOtherAmount(true)}
        >
          Other
        </AmountButton>
      </>
    );
  };

  const successContent = (
    <SuccessCard>
      <SuccessIconWrapper $themeColor={campaignDetails?.colorTheme}>
        <Icon as={FaCheckCircle as any} boxSize="4rem" />
      </SuccessIconWrapper>
      <SuccessTitle>Thank You!</SuccessTitle>
      <SuccessText>Your contribution is greatly appreciated.</SuccessText>
      <Heading size="md" mb={4}>
        Help Spread The Word
      </Heading>
      <ShareButton
        $themeColor={campaignDetails?.colorTheme}
        $variant="primary"
        onClick={handleShareClick}
      >
        <Icon as={FaShare as any} />
        Copy Share Link
      </ShareButton>
    </SuccessCard>
  );

  const statsCardContent = (
    <StatsCard $themeColor={campaignDetails?.colorTheme} $isMobile={isMobile}>
      <StatsHeader $themeColor={campaignDetails?.colorTheme}>
        <AmountRow>
          <RaisedAmount $isMobile={isMobile}>
            ${formatNumber(Math.round(totalRaised / 100))}
          </RaisedAmount>
          <GoalAmount $progressBarColor={progressBarColor}>
            of $
            {formatNumber(Math.round((campaignDetails?.goalAmount ?? 0) / 100))}
          </GoalAmount>
        </AmountRow>

        <ProgressBarContainer $themeColor={campaignDetails?.colorTheme}>
          <ProgressBar
            value={totalRaised}
            max={campaignDetails?.goalAmount ?? 0}
            color={progressBarColor}
          />
        </ProgressBarContainer>

        <Show when={(campaignDetails?.totalContributors ?? 0) > 0}>
          <ParticipantsCount $iconColor={progressBarColor}>
            <Icon as={FaUsers as any} />
            {campaignDetails?.totalContributors} participants
          </ParticipantsCount>
        </Show>
      </StatsHeader>

      <Show when={!!campaignDetails?.bonus}>
        <Flex justifyContent="center" py={4}>
          <BonusBadge $themeColor={campaignDetails?.colorTheme}>
            ❤️ ${formatNumber(Math.round((campaignDetails?.bonus ?? 0) / 100))}{" "}
            in sponsor matches!
          </BonusBadge>
        </Flex>
      </Show>

      <RecentDonationsSection>
        <RecentDonationsTicker
          messages={campaignDetails?.recents ?? []}
          themeColor={campaignDetails?.colorTheme}
        />
      </RecentDonationsSection>
    </StatsCard>
  );

  const donationContent = (
    <DonationCard
      ref={donateRef}
      $isMobile={isMobile}
      $themeColor={campaignDetails?.colorTheme}
    >
      <Show
        when={!gettingLink}
        whenFalseContent={
          <SpinnerContainer>
            <Spinner
              size="lg"
              color={campaignDetails?.colorTheme ?? colors.primary}
              thickness="4px"
            />
            <Text>Preparing your checkout...</Text>
          </SpinnerContainer>
        }
      >
        <Show when={!showSuccess} whenFalseContent={successContent}>
          <DonationCardHeader>
            <DonationCardTitle>Make Your Impact</DonationCardTitle>
            <Show when={!!campaignDetails?.expires}>
              <CountdownTimer
                colorTheme={campaignDetails?.colorTheme ?? colors.primary}
                expirationUtc={String(campaignDetails?.expires)}
              />
            </Show>
          </DonationCardHeader>

          <Show when={isMobile}>
            <Box mb={6}>{statsCardContent}</Box>
          </Show>

          <Tabs
            variant="soft-rounded"
            colorScheme="gray"
            onChange={(num) =>
              setGiveInterval(num === 0 ? "one-time" : "monthly")
            }
          >
            <TabList bg={colors.black + "05"} p={1} borderRadius="lg">
              <Tab
                flex={1}
                fontWeight={600}
                _selected={{
                  bg: campaignDetails?.colorTheme ?? colors.primary,
                  color: "white",
                }}
              >
                One Time
              </Tab>
              <Tab
                flex={1}
                fontWeight={600}
                _selected={{
                  bg: campaignDetails?.colorTheme ?? colors.primary,
                  color: "white",
                }}
              >
                Monthly
              </Tab>
            </TabList>
          </Tabs>

          <Show
            when={!showOtherAmount}
            whenFalseContent={
              <OtherAmount
                themeColor={campaignDetails?.colorTheme ?? colors.primary}
                onSubmit={(amount: number) => handlePayment(amount)}
                onCancel={() => setShowOtherAmount(false)}
              />
            }
          >
            <AmountGrid $isMobile={isMobile}>
              {renderAmountButtons()}
            </AmountGrid>
          </Show>

          <OptionsSection $themeColor={campaignDetails?.colorTheme}>
            <CheckboxGroup>
              <OptionRow
                $hasBottom
                $isMobile={isMobile}
                onClick={() => setIsCoveringFees(!isCoveringFees)}
              >
                <OptionLeft>
                  <OptionIcon $themeColor={campaignDetails?.colorTheme}>
                    <Icon as={FaShieldAlt as any} />
                  </OptionIcon>
                  <OptionContent>
                    <OptionLabel>Cover processing fees</OptionLabel>
                    <OptionDescription>
                      100% of your donation goes to the organization
                    </OptionDescription>
                  </OptionContent>
                </OptionLeft>
                <Checkbox
                  isChecked={isCoveringFees}
                  onChange={(e) => {
                    e.stopPropagation();
                    setIsCoveringFees(e.target.checked);
                  }}
                  colorScheme="gray"
                  size="lg"
                  pointerEvents="none"
                />
              </OptionRow>

              <OptionRow
                $hasBottom
                $isMobile={isMobile}
                onClick={onTipModalOpen}
              >
                <OptionLeft>
                  <OptionIcon $themeColor={campaignDetails?.colorTheme}>
                    <Icon as={FaHeart as any} />
                  </OptionIcon>
                  <OptionContent>
                    <OptionLabel>Support SkipGive</OptionLabel>
                    <OptionDescription>
                      {isTipping
                        ? `You are supporting SkipGive with a ${selectedTipPercentage}% tip`
                        : "Help us keep this free service running!"}
                    </OptionDescription>
                  </OptionContent>
                </OptionLeft>
                <Checkbox
                  isChecked={isTipping}
                  colorScheme="gray"
                  size="lg"
                  pointerEvents="none"
                />
              </OptionRow>

              <OptionRow
                $isMobile={isMobile}
                onClick={() => setGiveAnon(!giveAnon)}
              >
                <OptionLeft>
                  <OptionIcon $themeColor={campaignDetails?.colorTheme}>
                    <Icon as={FaUserSecret as any} />
                  </OptionIcon>
                  <OptionContent>
                    <OptionLabel>Give anonymously</OptionLabel>
                    <OptionDescription>
                      Your name won't be shown publicly
                    </OptionDescription>
                  </OptionContent>
                </OptionLeft>
                <Checkbox
                  isChecked={giveAnon}
                  onChange={(e) => {
                    e.stopPropagation();
                    setGiveAnon(e.target.checked);
                  }}
                  colorScheme="gray"
                  size="lg"
                  pointerEvents="none"
                />
              </OptionRow>
            </CheckboxGroup>
          </OptionsSection>

          <ShareSection $themeColor={campaignDetails?.colorTheme}>
            <Text
              fontSize="0.9rem"
              fontWeight={600}
              mb={3}
              color={colors.black + "80"}
            >
              Spread The Word
            </Text>
            <ShareButton
              $themeColor={campaignDetails?.colorTheme}
              $variant="secondary"
              onClick={handleShareClick}
            >
              <Icon as={FaShare as any} />
              Copy Share Link
            </ShareButton>
          </ShareSection>
        </Show>
      </Show>
    </DonationCard>
  );

  const campaignInfo = (
    <CampaignInfoSection $isMobile={isMobile}>
      <Show when={!showSuccess || !isMobile}>
        <HeroSection>
          <CampaignLogo>
            <Image
              size={isMobile ? "5rem" : "7rem"}
              src={campaignDetails?.logo}
            />
          </CampaignLogo>
          <CampaignTitle $isMobile={isMobile}>
            {campaignDetails?.name}
          </CampaignTitle>
          <CampaignDescription $isMobile={isMobile}>
            {campaignDetails?.description}
          </CampaignDescription>
          <Show when={!!campaignDetails?.tagline}>
            <Box mt={4}>
              <Badge
                themeColor={campaignDetails?.colorTheme}
                text={campaignDetails?.tagline ?? ""}
              />
            </Box>
          </Show>
          <Show when={isMobile}>
            <Box mt={4}>
              <Button
                buttonType="primary"
                label="Donate Now"
                type="button"
                style={{
                  padding: "1.25rem 3rem",
                  fontSize: "1.1rem",
                  width: "100%",
                }}
                onClick={scrollToDonate}
                themeColor={campaignDetails?.colorTheme ?? colors.primary}
              />
            </Box>
          </Show>

          <PoweredByBadge $isMobile={isMobile}>
            <Tag
              size="lg"
              colorScheme="blackAlpha"
              borderRadius="full"
              onClick={() => window.open("https://skipgive.com", "_blank")}
              style={{ cursor: "pointer" }}
            >
              <Avatar src={skipGiveIcon} size="xs" ml={-1} mr={2} />
              <TagLabel>Powered by SkipGive</TagLabel>
            </Tag>
          </PoweredByBadge>
        </HeroSection>

        <Show when={!isMobile}>{statsCardContent}</Show>
      </Show>
    </CampaignInfoSection>
  );

  return (
    <>
      <Show
        when={
          !!campaignDetails?.alertTitle &&
          !!campaignDetails.alertDescription &&
          !!campaignDetails.alertImage &&
          !showSuccess
        }
      >
        <AlertMessage
          title={campaignDetails?.alertTitle as string}
          description={campaignDetails?.alertDescription as string}
          image={campaignDetails?.alertImage as string}
          buttonLabel={campaignDetails?.alertButtonLabel ?? undefined}
          themeColor={campaignDetails?.colorTheme}
          expiration={String(campaignDetails?.expires)}
          campaignId={routeParams.id ?? ""}
        />
      </Show>

      <Show
        when={!!campaignDetails}
        whenFalseContent={
          <LoadingPage>
            <Spinner size="xl" color={colors.primary} />
          </LoadingPage>
        }
      >
        <PageWrapper
          $isMobile={isMobile}
          $themeColor={campaignDetails?.colorTheme}
        >
          <Header
            $isMobile={isMobile}
            $themeColor={campaignDetails?.colorTheme}
          >
            <HeaderContent>
              <OrgInfo
                onClick={() =>
                  window.open(
                    campaignDetails?.organizationWebsite ?? "",
                    "_blank"
                  )
                }
              >
                <Image
                  src={campaignDetails?.organizationLogo}
                  size={isMobile ? "2.5rem" : "3rem"}
                />
                <OrgName $isMobile={isMobile}>
                  {campaignDetails?.organization}
                </OrgName>
              </OrgInfo>

              {!isMobile && (
                <ShareButton
                  $themeColor={campaignDetails?.colorTheme}
                  $variant="secondary"
                  onClick={handleShareClick}
                  style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                >
                  <Icon as={FaShare as any} boxSize={3} />
                  Share
                </ShareButton>
              )}
              {isMobile && (
                <IconButton
                  aria-label="Share campaign"
                  icon={<Icon as={FaShare as any} />}
                  size="sm"
                  variant="outline"
                  colorScheme="whiteAlpha"
                  color="white"
                  onClick={handleShareClick}
                />
              )}
            </HeaderContent>
          </Header>

          <MainContent $isMobile={isMobile}>
            {campaignInfo}
            {donationContent}
          </MainContent>
        </PageWrapper>
      </Show>

      <Modal
        isOpen={isTipModalOpen}
        onClose={onTipModalClose}
        isCentered
        size={isMobile ? "full" : "md"}
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent as={StyledModalContent} $themeColor={campaignDetails?.colorTheme} $isMobile={isMobile}>
          <ModalHeader position="relative" zIndex={1}>Support SkipGive</ModalHeader>
          <ModalCloseButton position="absolute" top={4} right={4} zIndex={10} />
          <ModalBody
            pb={6}
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
            zIndex={1}
          >
            <Box maxW="500px" w="100%" position="relative" zIndex={1}>
              <TipModalContent>
                <TipModalLogo $themeColor={campaignDetails?.colorTheme}>
                  <Image src={skipGiveIcon} size="80px" />
                </TipModalLogo>
                <TipModalTitle>Help Keep This Free</TipModalTitle>
                <TipModalDescription>
                  SkipGive is a free platform that helps nonprofits raise funds
                  without any monthly fees. Your tip helps us keep it running.
                </TipModalDescription>
                <TipOptionsGrid>
                  <TipOptionButton
                    $isSelected={selectedTipPercentage === 5}
                    $themeColor={campaignDetails?.colorTheme}
                    onClick={() => handleTipSelection(5)}
                  >
                    5%
                  </TipOptionButton>
                  <TipOptionButton
                    $isSelected={selectedTipPercentage === 10}
                    $themeColor={campaignDetails?.colorTheme}
                    onClick={() => handleTipSelection(10)}
                  >
                    10%
                  </TipOptionButton>
                  <TipOptionButton
                    $isSelected={selectedTipPercentage === 15}
                    $themeColor={campaignDetails?.colorTheme}
                    onClick={() => handleTipSelection(15)}
                  >
                    15%
                  </TipOptionButton>
                  <TipOptionButton
                    $isSelected={selectedTipPercentage === 20}
                    $themeColor={campaignDetails?.colorTheme}
                    onClick={() => handleTipSelection(20)}
                  >
                    20%
                  </TipOptionButton>
                </TipOptionsGrid>
                <TipOptionButton
                  $isSelected={selectedTipPercentage === 0}
                  $isNoTip
                  onClick={() => handleTipSelection(0)}
                  style={{ width: "100%", gridColumn: "1 / -1" }}
                >
                  No tip
                </TipOptionButton>
              </TipModalContent>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
